import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faExternalLinkAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateAccount } from 'api/account';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import { useAppState } from 'customHooks/useAppState';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Account } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface SlugSettingsFormValues {
    slug: string;
}

const SlugSettings = () => {
    const toast = useToast();
    const [isEditing, setIsEditing] = useState(false);
    const { apiUrl } = useAppState();
    const { accountId, slug: accountSlug } = useCurrentAccount();

    const methods = Form.useGivebutterForm<SlugSettingsFormValues>({
        defaultValues: {
            slug: accountSlug || '',
        },
    });
    const { control, handleSubmit, path, setError, resetFormButton, reset, watch } = methods;
    const slug = watch('slug');

    const { mutate: submit, isPending: isLoading } = useMutation<
        Account,
        GivebutterAxiosError,
        SlugSettingsFormValues
    >({
        mutationFn: values => updateAccount(accountId, values),
        onSuccess: (response: Account) => {
            toast.success('Updated Landing Page URL!');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
            setIsEditing(false);
            reset({ slug: response.slug ?? '' });
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            const errors = data?.errors;

            toast.error(data?.message ?? 'Failed to update Landing Page URL');

            if (typeof errors === 'object') {
                for (let field in errors) {
                    setError(field as keyof SlugSettingsFormValues, {
                        message: errors[field][0],
                    });
                }
            }
            resetFormButton();
        },
    });

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading>Landing Page</SettingsCard.Heading>
                <p className="text-sm text-gray-700">
                    Your landing page displays all of your campaigns on Givebutter in one easy
                    place.
                </p>
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide>
                <Form.Provider {...methods}>
                    <form
                        className="flex flex-col items-start gap-3 rounded-lg bg-green-100 p-4"
                        onSubmit={handleSubmit(values => submit(values))}
                    >
                        {isEditing ? (
                            <>
                                <div className="flex flex-wrap items-center gap-1 lg:flex-nowrap">
                                    <span className="text-sm font-medium text-gray-700">
                                        {apiUrl}/
                                    </span>
                                    <div className="flex flex-col gap-2">
                                        <Form.Label className="sr-only" name={path('slug')}>
                                            Landing Page URL
                                        </Form.Label>
                                        <Form.TextInputRoot control={control} name="slug">
                                            <Form.TextInputForm autoFocus />
                                        </Form.TextInputRoot>
                                    </div>
                                </div>
                                <Form.Error name={path('slug')} />
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        color="neutral"
                                        disabled={isLoading}
                                        onClick={() => {
                                            setIsEditing(false);
                                            reset({ slug: accountSlug ?? '' });
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Form.SubmitButton isSubmitting={isLoading} size="sm">
                                        Save
                                    </Form.SubmitButton>
                                </div>
                            </>
                        ) : (
                            <>
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Open landing page in a new tab or window"
                                    href={`${apiUrl}/${slug}`}
                                    className="flex items-baseline gap-3 text-sm text-gray-700"
                                >
                                    {apiUrl}/{slug}
                                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                                </a>
                                <Button
                                    variant="none"
                                    color="success"
                                    size="sm"
                                    className="font-medium"
                                    onClick={event => {
                                        event.preventDefault();
                                        setIsEditing(true);
                                    }}
                                >
                                    Customize link
                                </Button>
                            </>
                        )}
                    </form>
                </Form.Provider>
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

export { SlugSettings };
